import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  NativeSelect,
  Typography,
  Select,
  Stack,
  CardContent,
  Card,
  Divider,
  styled,
} from '@mui/material';
// import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import moment from 'moment';

import useTable from '../../hooks/useTable';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { TableSkeleton } from '../../components/table';

import conObj from '../../utils/connection-assistant';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';

import { fDate } from '../../utils/formatTime';
import { fCurrency } from '../../utils/formatNumber';
import getPercentage from '../../utils/getPercentage';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';

const _filters = {
  dateFrom: '',
  dateTo: '',
  transactionType: '',
};

const InputBox = styled(Stack)(({ theme }) => ({
  background: 'rgb(8, 15, 28)',
  border: '1px solid rgb(32, 46, 65)',
  padding: '.375rem .75rem',
  borderRadius: '.25rem',
  cursor: 'pointer',
  alignItems: 'center',
}));

const DateText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#fff',
  fontSize: '.75rem',
}));

const TransactionHistory = () => {
  const { translate } = useLocales();

  const { memberBalance, withdrawRequirement } = useSelector((x) => x.lookup);

  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalRecord, setTotalRecord] = useState(0);

  const [filters, setFilters] = useState(_filters);

  const [isLoading, setIsLoading] = useState(false);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);

  const [amountInThousandths, setAmountInThousandths] = useState(false);

  const [dateRange, setDateRange] = useState(null);

  const pageLimit = 5;

  const {
    dense,
    // page,
    order,
    orderBy,
    rowsPerPage,
    // setPage,
    //
    // selected,
    // setSelected,
    // onSelectRow,
    // onSelectAllRows,
    //
    onSort,
    // onChangeDense,
    // onChangePage,
    // onChangeRowsPerPage,
  } = useTable();

  useEffect(() => {
    if (memberBalance?.currencyCode.toUpperCase() === 'MMK' || memberBalance?.currencyCode.toUpperCase() === 'VND') {
      setAmountInThousandths(true);
    }
  }, [memberBalance]);

  useEffect(() => {
    getTransactionHistory();
  }, [page, filters]);

  useEffect(() => {
    if (dateRange) {
      if (dateRange?.start) {
        setFilters((prevState) => ({ ...prevState, dateFrom: dateRange?.start }));
      }
      if (dateRange?.end) {
        setFilters((prevState) => ({ ...prevState, dateTo: dateRange?.end }));
      }
    }
  }, [dateRange]);

  const getTransactionHistory = () => {
    setIsLoading(true);
    const requestData = {
      'named-date_from': filters?.dateFrom ? moment(filters?.dateFrom).format('YYYY-MM-DD') : '',
      'named-date_to': filters?.dateTo ? moment(filters?.dateTo).format('YYYY-MM-DD') : '',
      'named-activity_type': filters?.transactionType || '', // deposit/withdraw/bonus/rebate/adjustment
      // ...(filters?.transactionType === 'deposit' ? { 'named-is_deposit': 'deposit' } : {}),
      // ...(filters?.transactionType === 'withdraw' ? { 'named-is_withdraw': 'withdraw' } : {}),
    };

    conObj
      .post(`transactions/index.json?page=${page}&limit=${pageLimit}`, requestData)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            setTableData(res.data?.data?.data);
            setTotalRecord(res.data?.data?.page.total_data);
          }
        }
      })
      .catch((err) => {
        console.log(`Error: ${err.message}`);
      })
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleFilterChange = (name, val) => {
    setFilters((prevState) => ({ ...prevState, [name]: val }));
  };

  const denseHeight = dense ? 60 : 80;

  const isNotFound = (!tableData.length && !!filters) || (!isLoading && !tableData.length);

  // NEW STYLED START
  const StyledCard = styled(Card)({
    borderRadius: '8px 8px 0 0',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#2e2e2e',
    '& > :last-child': {
      padding: '10px 20px',
    },
  });

  const StyledCardContent = styled(CardContent)({
    padding: '10px 20px',
    '&:last-child': {
      padding: '10px 20px',
    },
  });

  const StyledCardSmallText = styled('p')({
    margin: 0,
    padding: 0,
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '10px',
  });

  const StyledCardBottom = styled(Card)({
    borderRadius: '0 0 8px 8px',
    boxShadow: '373644',
    background: '#1a1a1a',
    '& > :last-child': {
      padding: '10px 20px',
    },
  });

  const StyledCardTag = styled('div')(({ status }) => ({
    borderRadius: '4px',
    backgroundColor: getStatusContanerColor(status.toUpperCase()),
    fontSize: '3px',
    padding: '2px 5px',
    color: getStatusLabelColor(status.toUpperCase()),
    width: 'fit-content',
  }));

  const EmptyContainer = styled(Stack)({
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#383649',
    color: 'rgba(255, 255, 255, 0.4)',
  });

  const StyledButton = styled(Button)({
    background: 'linear-gradient(165deg, rgb(0, 199, 199) -17.16%, rgb(19, 38, 216) 91.36%)',
    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 4px 4px 0px',
    color: '#fff',
  });

  const StyledButtonNav = styled(Button)({
    borderColor: '#FCD509',
    backgroundColor: '#FCD509',
    '&:hover': {
      borderColor: '#FCD509',
      backgroundColor: 'rgba(255, 255, 0, 0.1)',
    },
    '&:focus': {
      borderColor: '#FCD509',
      backgroundColor: 'rgba(255, 255, 0, 0.1)',
      outline: 'none',
    },
  });
  // NEW STYLED END

  // TAG CONTAINER CHANGING OF COLOR DEPENDS ON THE STATUS
  const getStatusContanerColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'rgba(66, 165, 245, 0.1)';
      case 'APPROVED':
        return 'rgba(0, 255, 0, 0.1)';
      case 'CANCELLED':
        return 'rgba(255, 255, 255, 0.08)';
      case 'REJECTED':
        return 'rgba(255, 36, 0, 0.1)';
      default:
        return '#242F39';
    }
  };

  // TAG LABEL CHANGING OF COLOR DEPENDS ON THE STATUS
  const getStatusLabelColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#42a5f5';
      case 'APPROVED':
        return '#00FF00';
      case 'CANCELLED':
        return 'rgba(255, 255, 255, 0.7)';
      case 'REJECTED':
        return '#ff2400';
      default:
        return '#242F39';
    }
  };

  return (
    <Page title="Transaction History" sx={{ p: { xs: 1 } }}>
      <Box component={Stack} spacing={2}>
        {/* Turnover Requirement - uncomment when needed */}
        {/* {(withdrawRequirement?.calculation_type === 'turnover' ||
          withdrawRequirement?.calculation_type === 'winover') &&
          withdrawRequirement?.requirement !== 0 && (
            <>
              <Box sx={{ backgroundColor: '#383649', borderRadius: 1, p: { xs: 1 } }} mb={3}>
                <Typography sx={{ color: '#fff' }}>
                  {`${
                    withdrawRequirement?.calculation_type === 'turnover' ? translate('turnover') : translate('balance')
                  } ${translate('requirement')}`}
                </Typography>
                <Stack direction="column" sx={{ p: 1, border: '1px solid yellow', borderRadius: 1, width: '100%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={getPercentage(withdrawRequirement?.current, withdrawRequirement?.requirement)}
                    color="warning"
                    sx={{ height: '20px', borderRadius: 2 }}
                  />

                  <Typography sx={{ color: '#fff' }}>
                    {fCurrency(withdrawRequirement?.current)} / {fCurrency(withdrawRequirement?.requirement)}
                  </Typography>
                </Stack>
              </Box>
            </>
          )} */}

        <Box sx={{ width: '100%' }}>
          <Stack
            direction={{ xl: 'row', lg: 'row', md: 'row', sm: 'row', xs: 'column' }}
            spacing={1}
            sx={{ my: '10px' }}
          >
            <Grid container spacing={'0.5rem'}>
              <Grid item xs={6}>
                <InputBox onClick={() => setOpenDateRangePicker(true)} direction="row">
                  <Stack sx={{ flex: 1 }}>
                    <DateText>{translate('date_from')}</DateText>
                    <DateText sx={{ fontSize: '.875rem' }}>
                      {filters?.dateFrom ? moment(filters?.dateFrom).format('DD/MM/YYYY') : translate('select_date')}
                    </DateText>
                  </Stack>
                  <Image src={require('../../assets/history/schedule.png')} sx={{ height: '26px', width: '26px' }} />
                </InputBox>
              </Grid>
              <Grid item xs={6}>
                <InputBox onClick={() => setOpenDateRangePicker(true)} direction="row">
                  <Stack sx={{ flex: 1 }}>
                    <DateText sx={{ fontWeight: 500, color: '#fff', fontSize: '.75rem' }}>
                      {translate('date_to')}
                    </DateText>
                    <DateText sx={{ fontSize: '.875rem' }}>
                      {filters?.dateTo ? moment(filters?.dateTo).format('DD/MM/YYYY') : translate('select_date')}
                    </DateText>
                  </Stack>
                  <Image src={require('../../assets/history/schedule.png')} sx={{ height: '26px', width: '26px' }} />
                </InputBox>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1}>
              <InputBox>
                <select
                  size="small"
                  value={filters.transactionType}
                  onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                  style={{
                    color: '#fff',
                    background: 'transparent',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '.75rem',
                    height: '100%',
                  }}
                >
                  <option value="">{translate('all')}</option>
                  <option value="deposit">{translate('deposit')}</option>
                  <option value="withdraw">{translate('withdraw')}</option>
                  <option value="bonus">{translate('bonus')}</option>
                  <option value="rebate">{translate('rebate')}</option>
                  <option value="adjustment">{translate('adjustment')}</option>
                </select>
              </InputBox>
              <StyledButton
                variant="text"
                size="small"
                onClick={() => {
                  setPage(1);
                  setFilters(_filters);
                }}
                sx={{ height: '34px' }}
              >
                {translate('clear')}
              </StyledButton>
            </Stack>
          </Stack>
          <Scrollbar>
            {tableData.length !== 0 ? (
              (isLoading ? [...Array(rowsPerPage)] : tableData).map((row, index) =>
                row ? (
                  <Stack key={row.id} mb={1}>
                    <StyledCard>
                      <StyledCardContent>
                        <Stack direction="row" spacing={1} justifyContent={'space-between'} alignItems={'center'}>
                          <Stack direction="row" spacing={1} alignItems={'center'}>
                            <Typography variant="body1">{row.title}</Typography>
                            <StyledCardTag status={row.transaction_status}>
                              <Typography variant="caption" sx={{ fontSize: '10px' }}>
                                {row.transaction_status.toUpperCase()}
                              </Typography>
                            </StyledCardTag>
                          </Stack>
                          <Typography variant="caption" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {fDate(row.date)}
                          </Typography>
                        </Stack>
                        <Stack direction="column">
                          <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {row.activity_type?.toUpperCase()}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          mt={1}
                          justifyContent={'space-between'}
                          alignItems={'center'}
                        >
                          <Stack direction="row" spacing={1} flex={1}>
                            <Stack direction="column" flex={1}>
                              <StyledCardSmallText variant="caption">
                                {translate('amount').toUpperCase()}
                              </StyledCardSmallText>
                              <Typography variant="body2">
                                {process.env.REACT_APP_CURRENCY_CODE} {parseFloat(row.amount?.value)}
                              </Typography>
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                            <Stack direction="column" flex={1}>
                              <StyledCardSmallText variant="caption">
                                {translate('bonus').toUpperCase()}
                              </StyledCardSmallText>
                              <Typography variant="body2">
                                {process.env.REACT_APP_CURRENCY_CODE} {parseFloat(row.bonus?.value)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </StyledCardContent>
                    </StyledCard>
                    <StyledCardBottom>
                      <Stack direction="column">
                        <Typography variant="caption" style={{ fontWeight: 300, color: '#FCD509' }}>
                          {translate('remarks').toUpperCase()}
                        </Typography>
                        <Typography variant="caption" style={{ fontWeight: 300 }}>
                          {row.remark || '-'}
                        </Typography>
                      </Stack>
                    </StyledCardBottom>
                  </Stack>
                ) : (
                  !isNotFound && <TableSkeleton key={index} sx={{ height: denseHeight }} />
                )
              )
            ) : (
              <EmptyContainer direction={'column'} alignItems={'center'} py={5}>
                <SvgIconStyle
                  src={`/assets/icons/others/folder.svg`}
                  sx={{ height: 90, width: 90, color: 'rgba(255, 255, 255, 0.2)' }}
                />
                <Typography variant="body2" sx={{ padding: 0 }}>
                  {translate('nothing_to_show')}
                </Typography>
              </EmptyContainer>
            )}
            {/* NEW PAGINATION START */}
            <Stack direction="row" spacing={1} my={2} justifyContent={'end'} alignItems={'center'}>
              <Stack direction="row" spacing={1} alignItems={'center'}>
                <StyledButtonNav
                  variant="contained"
                  disabled={page === 1}
                  onClick={() => {
                    if (page > 1) {
                      setPage(page - 1);
                    }
                  }}
                  sx={{ minWidth: 0, paddingX: 1 }}
                >
                  <SvgIconStyle src={`/assets/icons/pagination/arrow_back.svg`} sx={{ height: 20 }} />
                </StyledButtonNav>
                <Typography sx={{ paddingX: 2, paddingY: 0, margin: 0 }}>{page}</Typography>
                <StyledButtonNav
                  variant="contained"
                  disabled={tableData.length < pageLimit}
                  onClick={() => {
                    setPage(page + 1);
                  }}
                  sx={{ minWidth: 0, paddingX: 1 }}
                >
                  <SvgIconStyle src={`/assets/icons/pagination/arrow_next.svg`} sx={{ height: 20 }} />
                </StyledButtonNav>
              </Stack>
            </Stack>
          </Scrollbar>
        </Box>
      </Box>
      {openDateRangePicker && (
        <CustomDateRangePicker
          open={openDateRangePicker}
          setOpen={setOpenDateRangePicker}
          setDateRange={setDateRange}
          dateRange={dateRange}
        />
      )}
    </Page>
  );
};

export default TransactionHistory;
